import type { Dispatch, SetStateAction } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import vconst from 'utils/helpers/structs/vconst';
import { useObjectCard } from '../context/ObjectCardProvider';
import { useAuth } from 'context/AuthContext';
import { hidesPurchasePrices } from 'utils/helpers/access/hasUserAccess';
import TabGeneral from './tabs/TabGeneral';
import TabPrice from './tabs/TabPrice';
import TabWorkers from './tabs/TabWorkers';
import TabExpenses from './tabs/TabExpenses';
import { getExpendIncomeStatus, useExpendTeamInfo, type ExpendTeamInfoTab } from './model';

interface OffExpendTeamInfoProps {
    show: number | false;
    setShow: Dispatch<SetStateAction<number | false>>;
}

const OffExpendTeamInfo = ({ show, setShow }: OffExpendTeamInfoProps) => {
    const { t } = useTranslation(['Objects']);
    const { objectId, isEditLocked, fetchObject } = useObjectCard();
    const { auth } = useAuth();
    const hideCostAccess = hidesPurchasePrices(auth?.user);
    const { data, reload, activeTab, setActiveTab } = useExpendTeamInfo(objectId, show);

    const handleHide = () => {
        setShow(false);
    };

    const incomeStatus = getExpendIncomeStatus(data?.expend);
    const readOnly = Boolean(isEditLocked) || incomeStatus === vconst.incomeStatCancel;
    const expendId = Number(show) || Number(data?.expend?.ID) || 0;

    return (
        <Offcanvas
            className="off-import"
            show={show !== false}
            onHide={handleHide}
            placement="end"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('titleOperInfo')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {data?.expend ? (
                    <div className="off-expend-team-tabs d-flex flex-column h-100">
                        <Tabs
                            variant="underline"
                            id="off-expend-team-info-tab"
                            className="b-3 invoice-tab border-bottom px-3"
                            activeKey={
                                hideCostAccess && activeTab !== 'general' && activeTab !== 'price'
                                    ? 'general'
                                    : activeTab
                            }
                            onSelect={(key) => {
                                if (!key) return;
                                if (hideCostAccess && key !== 'general' && key !== 'price') return;
                                setActiveTab(key as ExpendTeamInfoTab);
                            }}
                        >
                            <Tab
                                eventKey="general"
                                title={
                                    <>
                                        <SolarIcon name="info-circle" className="me-2 fs-14" />
                                        {t('expendTeamWorkers.tabGeneral')}
                                    </>
                                }
                            >
                                <div className="off-create-team-content pt-3">
                                    <TabGeneral expendTeam={data} />
                                </div>
                            </Tab>
                            <Tab
                                eventKey="price"
                                title={
                                    <>
                                        <SolarIcon name="currency-dollar" className="me-2 fs-14" />
                                        {t('changePriceSale.tab')}
                                    </>
                                }
                            >
                                {expendId && data.expend ? (
                                    <TabPrice
                                        expendId={expendId}
                                        expend={data.expend}
                                        onSaved={async () => {
                                            await reload();
                                            await fetchObject();
                                        }}
                                    />
                                ) : null}
                            </Tab>
                            {!hideCostAccess && (
                            <Tab
                                eventKey="workers"
                                title={
                                    <>
                                        <SolarIcon name="people" className="me-2 fs-14" />
                                        {t('expendTeamWorkers.tabWorkers')}
                                    </>
                                }
                            >
                                {objectId && expendId ? (
                                    <TabWorkers
                                        objectId={Number(objectId)}
                                        expendId={expendId}
                                        expendTeam={data}
                                        readOnly={readOnly}
                                        onSaved={async () => {
                                            await reload();
                                            await fetchObject();
                                        }}
                                    />
                                ) : null}
                            </Tab>
                            )}
                            {!hideCostAccess && (
                            <Tab
                                eventKey="expenses"
                                title={
                                    <>
                                        <SolarIcon name="cash-coin" className="me-2 fs-14" />
                                        {t('expendTeamExpenses.tab')}
                                    </>
                                }
                            >
                                {objectId && expendId ? (
                                    <TabExpenses
                                        objectId={Number(objectId)}
                                        expendId={expendId}
                                        expendTeam={data}
                                        readOnly={readOnly}
                                        onSaved={reload}
                                    />
                                ) : null}
                            </Tab>
                            )}
                        </Tabs>
                    </div>
                ) : (
                    <ESpinner />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffExpendTeamInfo;
